import './RaceList.css';
import type { JSX, RefObject } from 'preact';
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { FlipList, mergeFrozenOrder } from '../../components/FlipList';
import { Pager } from '../../components/Pager';
import { SectionLabel } from '../../components/SectionLabel';
import { t } from '../../copy/strings';
import { RACE_TITLE_MIN_PX, type RaceModel, racePageSize } from '../../model/governors';
import type { LayoutMode, UfCode } from '../../model/types';
import { RaceRow } from './RaceRow';

/** Alturas medidas: a da lista inteira (regra do título) e a do corpo (linhas por página). */
interface Measured {
  root: number;
  body: number;
}

/**
 * Alturas da lista e do corpo por `ResizeObserver`, só onde a página depende da altura (desktop e TV). 0 = ainda sem
 * layout (aba oculta, primeira montagem, happy-dom): conta como não medido.
 */
function useMeasured(
  on: boolean,
  root: RefObject<HTMLElement | null>,
  body: RefObject<HTMLElement | null>,
): Measured | null {
  const [size, setSize] = useState<Measured | null>(null);
  useLayoutEffect(() => {
    if (!on) return;
    const measure = () => {
      const r = root.current?.clientHeight ?? 0;
      const b = body.current?.clientHeight ?? 0;
      setSize((s) => (s && s.root === r && s.body === b ? s : { root: r, body: b }));
    };
    measure();
    if (typeof ResizeObserver !== 'function') return;
    const ro = new ResizeObserver(measure);
    if (root.current) ro.observe(root.current);
    if (body.current) ro.observe(body.current);
    return () => ro.disconnect();
  }, [on]);
  return on ? size : null;
}

export interface RaceListProps {
  /** Disputas na ordem "das mais apertadas" (`listOrder`). */
  races: readonly RaceModel[];
  /** UF aberta (`aria-current="true"` na linha). */
  current: UfCode | null;
  /** Replay: sem FLIP e sem lavagem (§11.1 bloco 4 do painel). */
  replay: boolean;
  photos: boolean;
  mode: LayoutMode;
}

/**
 * "Disputas, das mais apertadas" (§11.1 `RaceList`): `SectionLabel` com o `Pager` ("1/1" com as setas desabilitadas
 * numa página só) e as `RaceRow` num `<ol>` (a ordem tem significado). Linhas de 78 px; por página, `floor(altura do
 * corpo / 78)` no desktop e na TV (TV: só a 1ª página, `Pager` oculto), 2 no `tablet`/`land` e todas no `flow` (sem
 * `Pager`). Com o mouse ou o foco dentro da lista, a ordem inteira fica congelada (as páginas não trocam de UF) até
 * sair; a reordenação entra com FLIP (`FlipList`). Mudar de página não move o foco (fica no botão do `Pager`).
 * Altura disponível < 70 px: o título some.
 */
export function RaceList({ races, current, replay, photos, mode }: RaceListProps) {
  const titleId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const flow = mode === 'flow';
  const tv = mode === 'tv';
  const measured = useMeasured(mode === 'desktop' || tv, rootRef, bodyRef);

  // Congelamento (§11.1 `RaceList` bloco 5, como na UfStrip): sobre a ordem inteira, antes de paginar.
  const [hover, setHover] = useState(false);
  const [focusIn, setFocusIn] = useState(false);
  const shown = useRef<UfCode[] | null>(null);
  const byUf = useMemo(() => new Map(races.map((r) => [r.uf, r] as const)), [races]);
  const live = races.map((r) => r.uf);
  const keys =
    (hover || focusIn) && shown.current ? (mergeFrozenOrder(shown.current, live) as UfCode[]) : (live as UfCode[]);
  useLayoutEffect(() => {
    shown.current = keys;
  });

  const bodyPx = measured && measured.body > 0 ? measured.body : null;
  const noTitle = measured !== null && measured.root > 0 && measured.root < RACE_TITLE_MIN_PX;
  const per = racePageSize(mode, bodyPx, keys.length);
  const total = Math.max(1, Math.ceil(keys.length / per));
  const [page, setPage] = useState(1);
  const p = tv ? 1 : Math.min(Math.max(1, page), total);
  // página fora do intervalo depois de mudar a altura: volta à última que existe
  useEffect(() => {
    if (page !== p && !tv) setPage(p);
  }, [page, p, tv]);
  const slice = keys.slice((p - 1) * per, p * per);
  const items = slice.flatMap((uf) => {
    const r = byUf.get(uf);
    return r ? [{ key: uf, race: r }] : [];
  });

  const onEnter = (e: JSX.TargetedPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse') setHover(true);
  };
  const onFocusOut = (e: JSX.TargetedFocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget;
    if (!(next instanceof Node && rootRef.current?.contains(next))) setFocusIn(false);
  };
  const pager = flow ? undefined : (
    <Pager
      page={p}
      total={total}
      onChange={setPage}
      showSingle
      prevLabel={t('gov.pager.prev')}
      nextLabel={t('gov.pager.next')}
    />
  );

  return (
    <div
      ref={rootRef}
      class={`race-list${noTitle ? ' race-list--no-title' : ''}`}
      data-testid="race-list"
      data-page={p}
      data-pages={total}
      data-per-page={per}
      data-state={hover || focusIn ? 'frozen' : undefined}
      onPointerEnter={onEnter}
      onPointerLeave={() => setHover(false)}
      onFocusIn={() => setFocusIn(true)}
      onFocusOut={onFocusOut}
    >
      {/* Alvo do foco se a linha focada sair da página (§15.2.2 regra 4): o cabeçalho, com tabindex -1. */}
      <div ref={headRef} class="race-list__head" tabIndex={-1}>
        <SectionLabel id={titleId} label={t('gov.list.title')} actions={pager} />
      </div>
      <div ref={bodyRef} class="race-list__body">
        {/* chave = página: trocar de página monta a lista nova sem FLIP (FLIP é só para reordenação, §11.1) */}
        <FlipList
          key={p}
          as="ol"
          items={items}
          replay={replay}
          focusFallback={headRef}
          class="race-list__rows"
          itemClass="race-list__item"
          ariaLabel={t('gov.list.title')}
          renderItem={({ race }) => (
            <RaceRow race={race} current={current === race.uf} replay={replay} photos={photos} />
          )}
        />
      </div>
    </div>
  );
}
